import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Loader2, User2, ShieldCheck, Briefcase, GraduationCap, FolderKanban, Tag, Eye, EyeOff, ExternalLink, CheckCircle2, AlertCircle, ScanSearch } from 'lucide-react';
import { getProfileEditor, saveProfileEditor, previewLinkedinVerification, verifyLinkUrl, type LinkedinPreviewResponse } from '../../services/api';

type TabKey = 'info' | 'vetting' | 'experience' | 'education' | 'projects' | 'skills' | 'other';

interface ProfileEditorModalProps {
  talentId: string;
  domain: string;
  /** Optional name shown in the header so we don't wait on the GET to render the title bar. */
  initialName?: string;
  onClose: () => void;
  onSaved?: () => void;
  embedded?: boolean;
  /** Which editor sub-tab to open on (default: 'info'). */
  initialTab?: TabKey;
  /** Scroll to and highlight a specific link field after load. Values: 'linkedin', 'scholar', 'teaching', 'additional'. */
  scrollToField?: string;
}

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'info', label: 'Info', icon: User2 },
  { key: 'vetting', label: 'Vetting', icon: ShieldCheck },
  { key: 'experience', label: 'Experience', icon: Briefcase },
  { key: 'education', label: 'Education', icon: GraduationCap },
  { key: 'projects', label: 'Projects', icon: FolderKanban },
  { key: 'skills', label: 'Skills', icon: Tag },
  { key: 'other', label: 'Other', icon: ShieldCheck },
];

/** Slide-style toggle component used per item and per section. */
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (next: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

function TextField({ label, value, onChange, type = 'text', placeholder }: { label: string; value: any; onChange: (v: any) => void; type?: 'text' | 'number'; placeholder?: string }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  );
}

/**
 * Text input for URL fields. Same look as TextField but with an external-link
 * icon button on the right that opens the URL in a new tab. Admins can edit
 * the value or click the icon to inspect the link — no copy/paste needed.
 */
function LinkField({ label, value, onChange, placeholder, onVerifyResult, linkType, talentName, claimText }: {
  label: string; value: any; onChange: (v: any) => void; placeholder?: string;
  onVerifyResult?: (result: { verdict: string; reason: string; newUrl?: string | null }) => void;
  linkType?: string; talentName?: string; claimText?: string;
}) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const isValid = /^https?:\/\//i.test(raw);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ verdict: string; reason: string; newUrl?: string | null } | null>(null);

  const handleVerify = async () => {
    if (!isValid) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await verifyLinkUrl(raw, linkType, talentName, claimText);
      setVerifyResult(res);
      onVerifyResult?.(res);
    } catch {
      setVerifyResult({ verdict: 'hidden', reason: 'request_failed' });
    } finally {
      setVerifying(false);
    }
  };

  const isOk = verifyResult?.verdict === 'ok';
  const isReplaced = verifyResult?.verdict === 'replaced';

  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      <div className="flex gap-1.5">
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => { onChange(e.target.value); setVerifyResult(null); }}
          placeholder={placeholder}
          className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={handleVerify}
          disabled={!isValid || verifying}
          title={isValid ? 'Run full link verification (HTTP + name match + LLM)' : 'Enter a valid http(s) URL to enable'}
          className={`shrink-0 inline-flex items-center justify-center px-2.5 h-[34px] rounded-md border text-xs font-medium transition-colors ${isValid
            ? 'border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
            : 'border-gray-100 dark:border-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'} disabled:opacity-50`}
        >
          {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />}
          <span className="ml-1">Verify</span>
        </button>
        <a
          href={isValid ? raw : undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!isValid}
          title={isValid ? 'Open in new tab' : 'Enter a valid http(s) URL to enable'}
          onClick={(e) => { if (!isValid) e.preventDefault(); }}
          className={`shrink-0 inline-flex items-center justify-center w-9 h-[34px] rounded-md border transition-colors ${isValid
            ? 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
            : 'border-gray-100 dark:border-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed pointer-events-none'}`}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      {verifyResult && (
        <div className={`mt-1.5 text-xs font-medium ${isOk ? 'text-green-600 dark:text-green-400' : isReplaced ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
          <div className="flex items-center gap-1.5">
            {isOk ? <><CheckCircle2 className="w-3.5 h-3.5" /> Verified</> :
             isReplaced ? <><AlertCircle className="w-3.5 h-3.5" /> Replaced — found better URL</> :
             <><AlertCircle className="w-3.5 h-3.5" /> Failed</>}
          </div>
          <div className="mt-0.5 font-normal text-gray-500 dark:text-gray-400">{verifyResult.reason}</div>
          {isReplaced && verifyResult.newUrl && (
            <button type="button" onClick={() => { onChange(verifyResult.newUrl); setVerifyResult(null); }}
              className="mt-1 text-blue-600 dark:text-blue-400 hover:underline">
              Use replacement: {verifyResult.newUrl}
            </button>
          )}
        </div>
      )}
    </label>
  );
}

const LINK_CHECK_REASON_LABELS: Record<string, string> = {
  http_error: 'Returned an HTTP error (e.g. 404 / 5xx)',
  soft_404: 'Page loaded but looks like a "not found" page',
  login_wall: 'Redirects to a login or auth wall',
  empty_body: 'Page loaded with no meaningful content',
  timeout: 'Took too long to respond',
  nav_error: 'Navigation failed (network / DNS / SSL)',
  no_response: 'Did not respond',
  exception: 'Verifier crashed while checking this link',
  unknown: 'Failed validation (reason not recorded)',
};

function LinkCheckWarning({
  reason,
  hidden,
  originalLink,
}: {
  reason?: string | null;
  hidden?: boolean;
  originalLink?: string | null;
}) {
  if (!reason) return null;

  // "replaced" is informational, not a warning: the pipeline swapped a broken URL
  // for a working one. Show the original so the admin can audit the swap.
  if (reason === 'replaced') {
    return (
      <div className="mt-2 flex items-start gap-2 rounded-md border border-blue-200 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-950/30 px-3 py-2 text-xs">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
        <div className="text-blue-900 dark:text-blue-200">
          <div className="font-medium">Auto-replaced by the link checker.</div>
          <div className="mt-0.5 text-blue-800/90 dark:text-blue-200/80">
            The original URL was unreachable, so the pipeline swapped in a working
            replacement. Review it before saving.
          </div>
          {originalLink && (
            <div className="mt-1 text-blue-800/90 dark:text-blue-200/80 break-all">
              Original: <span className="font-mono">{originalLink}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const detail = LINK_CHECK_REASON_LABELS[reason] || `Failed validation (${reason})`;
  return (
    <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs">
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
      <div className="text-amber-900 dark:text-amber-200">
        <div className="font-medium">Link couldn’t be validated. {detail}.</div>
        <div className="mt-0.5 text-amber-800/90 dark:text-amber-200/80">
          {hidden
            ? 'Hidden from customers by the link checker. If you re-enable it, customers may land on a broken or untrusted page. Verify it works first.'
            : 'Currently visible to customers despite failing validation. If the URL is still broken, customers will see a broken or untrusted page.'}
        </div>
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder }: { label: string; value: string | null | undefined; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  );
}

function StringList({ label, value, onChange, placeholder }: { label: string; value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const text = (value ?? []).join(', ');
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label} <span className="text-gray-400 font-normal">(comma-separated)</span></span>
      <input
        type="text"
        value={text}
        onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  );
}

function BulletList({ label, value, onChange }: { label: string; value: string[]; onChange: (v: string[]) => void }) {
  const text = (value ?? []).join('\n');
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label} <span className="text-gray-400 font-normal">(one per line)</span></span>
      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
        rows={Math.max(3, (value ?? []).length + 1)}
        className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
      />
    </label>
  );
}

function ItemCard({ children, hidden, onToggleHidden }: { children: React.ReactNode; hidden: boolean; onToggleHidden: (next: boolean) => void }) {
  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-800 p-4 transition-opacity ${hidden ? 'opacity-50' : ''}`}>
      <div className="flex justify-end mb-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          <span>{hidden ? 'Hidden' : 'Visible'}</span>
          <Toggle checked={!hidden} onChange={(next) => onToggleHidden(!next)} label="Show in customer portal" />
        </div>
      </div>
      {children}
    </div>
  );
}

function SectionHeader({ title, icon, hidden, onToggleHidden }: { title: string; icon: React.ReactNode; hidden?: boolean; onToggleHidden?: (next: boolean) => void }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{icon}<span>{title}</span></div>
      {onToggleHidden && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          <span>{hidden ? 'Section hidden' : 'Section visible'}</span>
          <Toggle checked={!hidden} onChange={(next) => onToggleHidden?.(!next)} label={`Toggle ${title}`} />
        </div>
      )}
    </div>
  );
}

export function ProfileEditorModal({ talentId, domain, initialName, onClose, onSaved, embedded, initialTab, scrollToField }: ProfileEditorModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Record<string, any> | null>(null);
  const [tab, setTab] = useState<TabKey>(initialTab || 'info');
  const [linkedinNeedsVerify, setLinkedinNeedsVerify] = useState(false);
  const scrollDoneRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProfileEditor(talentId, domain)
      .then((res) => { if (!cancelled) setProfile(res.profile || {}); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || err.message || 'Failed to load profile'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [talentId, domain]);

  useEffect(() => {
    if (!scrollToField || loading || !profile || scrollDoneRef.current) return;
    scrollDoneRef.current = true;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-link-type="${scrollToField}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-red-400', 'rounded-lg', 'transition-all');
        setTimeout(() => el.classList.remove('ring-2', 'ring-red-400', 'rounded-lg', 'transition-all'), 2000);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [scrollToField, loading, profile]);

  // Helpers to update profile immutably.
  const set = (key: string, value: any) => setProfile((p) => ({ ...(p ?? {}), [key]: value }));
  const setArrayItem = (key: string, index: number, patch: Record<string, any>) => {
    setProfile((p) => {
      const arr = Array.isArray(p?.[key]) ? [...p![key]] : [];
      arr[index] = { ...arr[index], ...patch };
      return { ...(p ?? {}), [key]: arr };
    });
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setError(null);
    // Whitelist the fields the backend accepts. Anything else is ignored.
    const EDITABLE = [
      'title', 'yearOfExperience', 'field', 'country', 'tShortSummary', 'tSummary', 'tSkills',
      'skills', 'workExperience', 'education', 'projects', 'certifications', 'additionalVerifications',
      'hobbies', 'languages', 'linkedInProfile', 'googleScholarURL', 'teachingOrResearchProfile',
      'linkedInVerification', 'scholarVerify',
      'summaryHidden', 'skillsHidden', 'hobbiesHidden', 'verificationsHidden',
      '_linkedInApifyData',
    ];
    const payload: Record<string, any> = {};
    for (const k of EDITABLE) if (k in profile) payload[k] = profile[k];
    try {
      await saveProfileEditor(talentId, domain, payload);
      onSaved?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const displayName = profile?.name || initialName || talentId;

  const content = (
    <div className={embedded ? 'flex flex-1 flex-col min-h-0' : 'bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col'} onClick={embedded ? undefined : (e) => e.stopPropagation()}>
      {!embedded && (
        <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Edit Profile: {displayName}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Configure what information is visible to companies.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
            <button
              onClick={handleSave}
              disabled={loading || saving || !profile || linkedinNeedsVerify}
              title={linkedinNeedsVerify ? 'Click Verify on LinkedIn URL before saving' : undefined}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save Changes
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
        </div>
      )}

        {/* Tabs */}
        <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 flex items-center gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            >
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
          )}
          {error && !loading && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">{error}</div>
          )}
          {!loading && !error && profile && (
            <>
              {tab === 'info' && <InfoTab profile={profile} set={set} talentId={talentId} domain={domain} onLinkedinNeedsVerifyChange={setLinkedinNeedsVerify} />}
              {tab === 'vetting' && <VettingTab profile={profile} set={set} setArrayItem={setArrayItem} />}
              {tab === 'experience' && (
                <ArraySection
                  title="Work Experience" icon={<Briefcase className="w-4 h-4" />}
                  arrayKey="workExperience" profile={profile} setArrayItem={setArrayItem}
                  fields={[
                    { key: 'company', label: 'Company' },
                    { key: 'position', label: 'Position' },
                    { key: 'startYear', label: 'Start Year', type: 'number' },
                    { key: 'startMonth', label: 'Start Month', type: 'number' },
                    { key: 'endYear', label: 'End Year', type: 'number' },
                    { key: 'endMonth', label: 'End Month', type: 'number' },
                  ]}
                  bulletField="description"
                />
              )}
              {tab === 'education' && (
                <ArraySection
                  title="Education" icon={<GraduationCap className="w-4 h-4" />}
                  arrayKey="education" profile={profile} setArrayItem={setArrayItem}
                  fields={[
                    { key: 'school', label: 'School' },
                    { key: 'degree', label: 'Degree' },
                    { key: 'level', label: 'Level' },
                    { key: 'startYear', label: 'Start Year', type: 'number' },
                    { key: 'endYear', label: 'End Year', type: 'number' },
                  ]}
                />
              )}
              {tab === 'projects' && (
                <ArraySection
                  title="Projects" icon={<FolderKanban className="w-4 h-4" />}
                  arrayKey="projects" profile={profile} setArrayItem={setArrayItem}
                  fields={[
                    { key: 'name', label: 'Name' },
                    { key: 'url', label: 'URL' },
                  ]}
                  bulletField="description"
                />
              )}
              {tab === 'skills' && <SkillsTab profile={profile} set={set} />}
              {tab === 'other' && <OtherTab profile={profile} set={set} setArrayItem={setArrayItem} />}
            </>
          )}
        </div>

      {embedded && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4 flex items-center justify-end gap-3">
          {error && <span className="text-sm text-red-600 dark:text-red-400 mr-auto">{error}</span>}
          <button
            onClick={handleSave}
            disabled={loading || saving || !profile || linkedinNeedsVerify}
            title={linkedinNeedsVerify ? 'Click Verify on LinkedIn URL before saving' : undefined}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save Changes
          </button>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      {content}
    </div>,
    document.body,
  );
}

function InfoTab({
  profile,
  set,
  talentId,
  domain,
  onLinkedinNeedsVerifyChange,
}: {
  profile: Record<string, any>;
  set: (k: string, v: any) => void;
  talentId: string;
  domain: string;
  onLinkedinNeedsVerifyChange: (needs: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField label="Title" value={profile.title} onChange={(v) => set('title', v)} />
        <TextField label="Field" value={profile.field} onChange={(v) => set('field', v)} />
        <TextField label="Years of Experience" type="number" value={profile.yearOfExperience} onChange={(v) => set('yearOfExperience', v)} />
        <TextField label="Country" value={profile.country} onChange={(v) => set('country', v)} />
      </div>
      <StringList label="Tags (tSkills)" value={profile.tSkills ?? []} onChange={(v) => set('tSkills', v)} placeholder="e.g. Python, TypeScript, React" />
      <div className="space-y-3">
        <SectionHeader
          title="Summary"
          icon={<User2 className="w-4 h-4" />}
          hidden={!!profile.summaryHidden}
          onToggleHidden={(next) => set('summaryHidden', next)}
        />
        <TextArea label="Short Summary (tShortSummary)" value={profile.tShortSummary} onChange={(v) => set('tShortSummary', v)} rows={2} />
        <BulletList label="Summary Bullets (tSummary)" value={profile.tSummary ?? []} onChange={(v) => set('tSummary', v)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div data-link-type="linkedin">
          <LinkedInField
            value={profile.linkedInProfile}
            verification={profile.linkedInVerification}
            onChange={(url, verification, apifyData) => {
              set('linkedInProfile', url);
              if (verification !== undefined) set('linkedInVerification', verification);
              if (apifyData !== undefined) set('_linkedInApifyData', apifyData);
            }}
            onNeedsVerifyChange={onLinkedinNeedsVerifyChange}
            talentId={talentId}
            domain={domain}
          />
          <LinkCheckWarning
            reason={profile.linkedInProfileLinkCheckReason}
            hidden={!!profile.linkedInProfileHidden}
          />
        </div>
        <div data-link-type="scholar">
          <LinkField label="Google Scholar URL" value={profile.googleScholarURL} onChange={(v) => set('googleScholarURL', v)}
            linkType="scholar" talentName={profile.name || profile.title}
            onVerifyResult={(r) => {
              const ok = r.verdict === 'ok';
              set('googleScholarHidden', !ok);
              set('googleScholarLinkCheckReason', ok ? null : (r.reason || 'unknown'));
              if (r.verdict === 'replaced' && r.newUrl) { set('googleScholarURL', r.newUrl); set('googleScholarLinkCheckReason', 'replaced'); set('googleScholarHidden', false); }
            }}
          />
          <LinkCheckWarning
            reason={profile.googleScholarLinkCheckReason}
            hidden={!!profile.googleScholarHidden}
            originalLink={profile.googleScholarOriginalURL}
          />
        </div>
        {profile.teachingOrResearchProfile && (
          <div data-link-type="teaching">
            <ItemCard
              hidden={!!profile.teachingOrResearchProfile?.hidden}
              onToggleHidden={(h) => set('teachingOrResearchProfile', { ...(profile.teachingOrResearchProfile || {}), hidden: h })}
            >
              <LinkField
                label="Teaching / Research Profile"
                value={profile.teachingOrResearchProfile?.link}
                onChange={(v) => set('teachingOrResearchProfile', { ...(profile.teachingOrResearchProfile || {}), link: v })}
                linkType="teaching" talentName={profile.name || profile.title}
                onVerifyResult={(r) => {
                  const ok = r.verdict === 'ok';
                  const patch: any = { hidden: !ok, linkCheckReason: ok ? null : (r.reason || 'unknown') };
                  if (r.verdict === 'replaced' && r.newUrl) { patch.link = r.newUrl; patch.linkCheckReason = 'replaced'; patch.hidden = false; }
                  set('teachingOrResearchProfile', { ...(profile.teachingOrResearchProfile || {}), ...patch });
                }}
              />
              <LinkCheckWarning
                reason={profile.teachingOrResearchProfile?.linkCheckReason}
                hidden={!!profile.teachingOrResearchProfile?.hidden}
                originalLink={profile.teachingOrResearchProfile?.originalLink}
              />
            </ItemCard>
          </div>
        )}
      </div>
    </div>
  );
}

/** LinkedIn URL field with an inline "Verify" button.
 *
 * Flow:
 *  1. Admin types URL → field marked dirty → save is blocked (parent disables Save).
 *  2. Admin clicks Verify → backend hits Apify → verification auto-applied to profile state.
 *  3. Verify-result badges shown directly below the input. No Apply/Discard.
 *  4. If admin clears the URL → clear linkedInVerification too (so customer UI doesn't show stale verification).
 */
function LinkedInField({
  value,
  verification,
  onChange,
  onNeedsVerifyChange,
  talentId,
  domain,
}: {
  value: any;
  verification: any;
  onChange: (url: string, verification?: any, apifyData?: any) => void;
  onNeedsVerifyChange: (needs: boolean) => void;
  talentId: string;
  domain: string;
}) {
  const url = String(value ?? '');

  const hasStoredVerification = !!(
    verification &&
    (verification.Identity?.length ||
      verification.Workplace?.length ||
      verification.EducationalInstitution?.length ||
      verification.joinedDate)
  );

  // Track the URL that was last verified (or initially loaded with verification).
  // If the current URL differs, the field is dirty and save should be blocked.
  const [verifiedUrl, setVerifiedUrl] = useState<string | null>(url.trim() || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<LinkedinPreviewResponse | null>(null);

  const dirty = !!url.trim() && url.trim() !== (verifiedUrl ?? '').trim();

  // Bubble dirty state to parent (blocks Save when true).
  useEffect(() => {
    onNeedsVerifyChange(dirty);
  }, [dirty, onNeedsVerifyChange]);

  const runVerify = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await previewLinkedinVerification(talentId, domain, url);
      setLastResult(res);
      onChange(url, res.hasEvidence ? res.verification : null, res.apifyData ?? null);
      setVerifiedUrl(url);
      if (res.error) setError(res.error);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  // Live verification source: prefer last fetched result; fallback to stored.
  const liveHas = (() => {
    const src = lastResult?.verification ?? verification;
    return {
      identity: (src?.Identity?.length || 0) > 0,
      work: (src?.Workplace?.length || 0) > 0,
      education: (src?.EducationalInstitution?.length || 0) > 0,
    };
  })();
  const showBadges = !dirty && (hasStoredVerification || (lastResult && lastResult.hasEvidence));
  const showNoEvidence = !dirty && lastResult && !lastResult.hasEvidence && !lastResult.error;

  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">LinkedIn URL</span>
      <div className="flex gap-1.5">
        <input
          type="text"
          value={url}
          onChange={(e) => {
            const next = e.target.value;
            if (!next.trim()) {
              // Clearing the URL → also clear stored verification (customer UI must not show stale).
              onChange(next, null, null);
              setVerifiedUrl(null);
              setLastResult(null);
              setError(null);
            } else {
              onChange(next);
              setLastResult(null);
            }
          }}
          placeholder="https://www.linkedin.com/in/..."
          className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          disabled={!url.trim() || busy || !dirty}
          onClick={runVerify}
          title={dirty ? 'Verify via LinkedIn (Apify)' : url.trim() ? 'Already verified' : 'Enter a URL first'}
          className="shrink-0 inline-flex items-center justify-center px-2.5 h-[34px] rounded-md border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          <span className="ml-1">{busy ? '…' : 'Verify'}</span>
        </button>
      </div>

      {/* Inline result / state */}
      {dirty && (
        <div className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">URL changed. click Verify before saving.</div>
      )}
      {showBadges && (
        <div className="mt-1.5 flex items-center gap-2 text-xs">
          <VBadge ok={liveHas.identity} label="Identity" />
          <VBadge ok={liveHas.work} label="Work" />
          <VBadge ok={liveHas.education} label="Education" />
        </div>
      )}
      {showNoEvidence && (
        <div className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">No evidence found — URL will be saved but customer UI will hide LinkedIn.</div>
      )}
      {error && (
        <div className="mt-1.5 text-xs text-red-700 dark:text-red-400">{error}</div>
      )}
    </label>
  );
}

/** Compact 1/0 verification badge: green check if present, gray dash if absent. */
function VBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? 'text-green-700 dark:text-green-400' : 'text-gray-400 dark:text-gray-600'}`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 inline-flex items-center justify-center">—</span>}
      <span>{label}</span>
    </span>
  );
}

function VettingTab({ profile, set, setArrayItem }: { profile: Record<string, any>; set: (k: string, v: any) => void; setArrayItem: (k: string, i: number, patch: Record<string, any>) => void }) {
  const additional = Array.isArray(profile.additionalVerifications) ? profile.additionalVerifications : [];
  return (
    <div className="space-y-5">
      <div>
        <SectionHeader
          title="Verifications"
          icon={<ShieldCheck className="w-4 h-4" />}
          hidden={!!profile.verificationsHidden}
          onToggleHidden={(next) => set('verificationsHidden', next)}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">Master toggle hides LinkedIn verification, Scholar verification, and Teaching/Research profile from the customer view.</p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Additional Verifications</div>
        </div>
        <div className="space-y-3">
          {additional.length === 0 && <p className="text-sm text-gray-400">No additional verifications.</p>}
          {additional.map((v: any, i: number) => (
            <ItemCard key={i} hidden={v?.hidden === true} onToggleHidden={(h) => setArrayItem('additionalVerifications', i, { hidden: h })}>
              <div className="space-y-2">
                <TextArea label="Text" value={v.text} onChange={(val) => setArrayItem('additionalVerifications', i, { text: val })} rows={2} />
                <LinkField label="Link" value={v.link} onChange={(val) => setArrayItem('additionalVerifications', i, { link: val })}
                  linkType="additional" talentName={profile.name || profile.title} claimText={v.text}
                  onVerifyResult={(r) => {
                    const ok = r.verdict === 'ok';
                    const patch: any = { hidden: !ok, linkCheckReason: ok ? null : (r.reason || 'unknown') };
                    if (r.verdict === 'replaced' && r.newUrl) { patch.link = r.newUrl; patch.linkCheckReason = 'replaced'; patch.hidden = false; }
                    setArrayItem('additionalVerifications', i, patch);
                  }}
                />
                <LinkCheckWarning reason={v?.linkCheckReason} hidden={v?.hidden === true} originalLink={v?.originalLink} />
              </div>
            </ItemCard>
          ))}
        </div>
      </div>
    </div>
  );
}

function ArraySection({
  title, icon, arrayKey, profile, setArrayItem, fields, bulletField,
}: {
  title: string;
  icon: React.ReactNode;
  arrayKey: string;
  profile: Record<string, any>;
  setArrayItem: (k: string, i: number, patch: Record<string, any>) => void;
  fields: { key: string; label: string; type?: 'text' | 'number' }[];
  bulletField?: string;
}) {
  const items = Array.isArray(profile[arrayKey]) ? profile[arrayKey] : [];
  return (
    <div>
      <SectionHeader title={title} icon={icon} />
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-gray-400">No entries.</p>}
        {items.map((it: any, i: number) => (
          <ItemCard key={i} hidden={it?.hidden === true} onToggleHidden={(h) => setArrayItem(arrayKey, i, { hidden: h })}>
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <TextField key={f.key} label={f.label} type={f.type} value={it[f.key]} onChange={(v) => setArrayItem(arrayKey, i, { [f.key]: v })} />
              ))}
            </div>
            {bulletField && (
              <div className="mt-3">
                <BulletList label="Description" value={Array.isArray(it[bulletField]) ? it[bulletField] : []} onChange={(v) => setArrayItem(arrayKey, i, { [bulletField]: v })} />
              </div>
            )}
          </ItemCard>
        ))}
      </div>
    </div>
  );
}

function SkillsTab({ profile, set }: { profile: Record<string, any>; set: (k: string, v: any) => void }) {
  const skills = Array.isArray(profile.skills) ? profile.skills : [];
  return (
    <div className="space-y-4">
      <SectionHeader
        title="Skills"
        icon={<Tag className="w-4 h-4" />}
        hidden={!!profile.skillsHidden}
        onToggleHidden={(next) => set('skillsHidden', next)}
      />
      <StringList label="Skill Tags (tSkills)" value={profile.tSkills ?? []} onChange={(v) => set('tSkills', v)} />
      <div>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Skills with years of experience</div>
        <div className="space-y-2">
          {skills.length === 0 && <p className="text-sm text-gray-400">No detailed skill entries.</p>}
          {skills.map((s: any, i: number) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={s?.name ?? ''}
                onChange={(e) => set('skills', skills.map((x: any, idx: number) => (idx === i ? { ...x, name: e.target.value } : x)))}
                placeholder="Skill"
                className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"
              />
              <input
                type="number"
                value={s?.yearsOfExperience ?? ''}
                onChange={(e) => set('skills', skills.map((x: any, idx: number) => (idx === i ? { ...x, yearsOfExperience: e.target.value === '' ? null : Number(e.target.value) } : x)))}
                placeholder="Years"
                className="w-24 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"
              />
              <button
                onClick={() => set('skills', skills.filter((_: any, idx: number) => idx !== i))}
                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                aria-label="Remove skill"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() => set('skills', [...skills, { name: '', yearsOfExperience: null }])}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            + Add skill
          </button>
        </div>
      </div>
    </div>
  );
}

function OtherTab({ profile, set, setArrayItem }: { profile: Record<string, any>; set: (k: string, v: any) => void; setArrayItem: (k: string, i: number, patch: Record<string, any>) => void }) {
  const certs = Array.isArray(profile.certifications) ? profile.certifications : [];
  const hobbies: string[] = Array.isArray(profile.hobbies) ? profile.hobbies : [];
  const naturalLangs: string[] = Array.isArray(profile.languages?.natural) ? profile.languages.natural : [];
  return (
    <div className="space-y-6">
      {/* Certifications */}
      <div>
        <SectionHeader title="Certifications" icon={<ShieldCheck className="w-4 h-4" />} />
        <div className="space-y-3">
          {certs.length === 0 && <p className="text-sm text-gray-400">No certifications.</p>}
          {certs.map((c: any, i: number) => (
            <ItemCard key={i} hidden={c?.hidden === true} onToggleHidden={(h) => setArrayItem('certifications', i, { hidden: h })}>
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Name" value={c.name} onChange={(v) => setArrayItem('certifications', i, { name: v })} />
                <TextField label="Issuer" value={c.issuer} onChange={(v) => setArrayItem('certifications', i, { issuer: v })} />
                <TextField label="Year" type="number" value={c.year} onChange={(v) => setArrayItem('certifications', i, { year: v })} />
                <LinkField label="URL" value={c.url} onChange={(v) => setArrayItem('certifications', i, { url: v })} />
              </div>
            </ItemCard>
          ))}
        </div>
      </div>

      {/* Hobbies */}
      <div>
        <SectionHeader
          title="Hobbies"
          icon={<Tag className="w-4 h-4" />}
          hidden={!!profile.hobbiesHidden}
          onToggleHidden={(next) => set('hobbiesHidden', next)}
        />
        <StringList label="Hobbies" value={hobbies} onChange={(v) => set('hobbies', v)} placeholder="Reading, Chess, Cycling" />
      </div>

      {/* Languages */}
      <div>
        <SectionHeader title="Languages" icon={<Tag className="w-4 h-4" />} />
        <StringList
          label="Spoken languages"
          value={naturalLangs}
          onChange={(v) => set('languages', { ...(profile.languages ?? {}), natural: v })}
          placeholder="English, Spanish"
        />
      </div>
    </div>
  );
}

export default ProfileEditorModal;
